<template>
  <b-modal
    ref="addReturnReasonModal"
    title="Ajouter une raison de retour"
    size="md"
    hide-footer
    @hidden="resetForm"
  >
    <!-- BL Display -->
    <div
      v-if="orderBl"
      class="mb-2 d-flex align-items-center justify-content-between"
    >
      <span class="font-weight-bold">Numéro BL :</span>
      <span class="bl-copy-area">
        <span class="bl-value">{{ orderBl }}</span>
        <b-button
          v-b-tooltip.hover
          title="Copier le BL"
          variant="link"
          size="sm"
          class="ml-1 p-0"
          @click="copyBlToClipboard"
        >
          <feather-icon
            icon="CopyIcon"
            size="16"
          />
        </b-button>
      </span>
    </div>

    <div
      v-if="isLoadingReasons"
      class="text-center py-3"
    >
      <b-spinner
        variant="primary"
        label="Chargement des raisons de retour..."
      />
      <p class="mt-2">
        Chargement des raisons de retour...
      </p>
    </div>

    <b-form
      v-else
      @submit.prevent="submitReturnReason"
    >
      <b-form-group
        label="Raison de retour"
        label-for="reason-type"
        description="Sélectionnez la raison du retour"
      >
        <b-form-select
          id="reason-type"
          v-model="selectedReasonType"
          :options="reasonTypeOptions"
          :disabled="isLoading"
          required
        >
          <template #first>
            <b-form-select-option
              :value="null"
              disabled
            >
              -- Sélectionnez une raison --
            </b-form-select-option>
          </template>
        </b-form-select>
      </b-form-group>

      <!-- Calendar input if reason requires datetime -->
      <b-form-group
        v-if="needsDateTime"
        label="Date et heure prévue"
        label-for="scheduled-date"
        description="Sélectionnez la date et l'heure si requis par le motif."
      >
        <b-form-datepicker
          id="scheduled-date-date"
          v-model="scheduledDateDate"
          locale="fr"
          :min="getMinDate()"
          :disabled="isLoading"
          class="mb-2"
        />
        <b-form-timepicker
          id="scheduled-date-time"
          v-model="scheduledDateTime"
          locale="fr"
          :hour12="false"
          :disabled="isLoading"
          :minute-step="5"
        />
      </b-form-group>

      <!-- Extra note textarea if reason requires additional note -->
      <b-form-group
        v-if="needsAdditionalNote"
        label="Note supplémentaire"
        label-for="additional-note"
        description="Ajoutez des détails supplémentaires si requis par le motif."
      >
        <b-form-textarea
          id="additional-note"
          v-model="additionalNote"
          rows="2"
          :placeholder="getAdditionalTextPlaceholder"
          :disabled="isLoading"
        />
      </b-form-group>

      <div class="d-flex justify-content-end">
        <b-button
          variant="outline-secondary"
          class="mr-2"
          @click="closeModal"
        >
          Annuler
        </b-button>
        <b-button
          type="submit"
          variant="primary"
          :disabled="!selectedReasonType || isLoading"
        >
          <b-spinner
            v-if="isLoading"
            small
            class="mr-1"
          />
          Ajouter
        </b-button>
      </div>
    </b-form>
  </b-modal>
</template>

<script>
import {
  BModal,
  BForm,
  BFormGroup,
  BFormSelect,
  BFormSelectOption,
  BButton,
  BSpinner,
  BFormTextarea,
  BFormDatepicker,
  BFormTimepicker,
} from 'bootstrap-vue'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'AddReturnReasonModal',
  components: {
    BModal,
    BForm,
    BFormGroup,
    BFormSelect,
    BFormSelectOption,
    BButton,
    BSpinner,
    BFormTextarea,
    BFormDatepicker,
    BFormTimepicker,
  },
  props: {
    orderId: {
      type: [Number, String],
      required: true,
    },
    orderBl: {
      type: [Number, String],
      required: false,
      default: '',
    },
  },
  data() {
    return {
      selectedReasonType: null,
      reasonTypes: [],
      isLoading: false,
      isLoadingReasons: false,
      scheduledDate: '',
      scheduledDateDate: '',
      scheduledDateTime: '',
      additionalNote: '',
    }
  },
  computed: {
    reasonTypeOptions() {
      return this.reasonTypes.map(reason => ({
        value: reason.id,
        text: reason.content,
      }))
    },
    selectedReasonObj() {
      return this.reasonTypes.find(r => r.id === this.selectedReasonType) || {}
    },
    needsDateTime() {
      return !!this.selectedReasonObj.require_datetime
    },
    needsAdditionalNote() {
      return !!this.selectedReasonObj.require_additional_note
    },
    getDateTimePlaceholder() {
      return this.selectedReasonObj.datetime_placeholder || 'Date et heure prévue'
    },
    getAdditionalTextPlaceholder() {
      return this.selectedReasonObj.additional_note_placeholder || 'Ajouter des détails supplémentaires...'
    },
  },
  watch: {
    selectedReasonType() {
      // Reset fields when reason changes
      this.scheduledDate = ''
      this.scheduledDateDate = ''
      this.scheduledDateTime = ''
      this.additionalNote = ''
      // If the new reason requires datetime, set default to today at 08:00
      if (this.needsDateTime) {
        const now = new Date()
        now.setHours(8, 0, 0, 0)
        this.scheduledDateDate = now.toISOString().slice(0, 10)
        this.scheduledDateTime = '08:00'
        this.scheduledDate = `${this.scheduledDateDate}T${this.scheduledDateTime}`
      }
    },
    scheduledDateDate(val) {
      if (val && this.scheduledDateTime) {
        this.scheduledDate = `${val}T${this.scheduledDateTime}`
      }
    },
    scheduledDateTime(val) {
      if (val && this.scheduledDateDate) {
        this.scheduledDate = `${this.scheduledDateDate}T${val}`
      }
    },
  },
  methods: {
    async show() {
      this.isLoadingReasons = true
      try {
        await this.loadReasonTypes()
        this.$refs.addReturnReasonModal.show()
      } catch (error) {
        this.showToast('error', 'Erreur', 'Impossible de charger les raisons de retour')
      } finally {
        this.isLoadingReasons = false
      }
    },

    async loadReasonTypes() {
      try {
        const response = await axios.get('/api/return-vouchers/reason-types/')
        this.reasonTypes = response.data.results || response.data
      } catch (error) {
        console.error('Error loading return reason types:', error)
        throw error
      }
    },

    async submitReturnReason() {
      if (!this.selectedReasonType) return

      this.isLoading = true
      try {
        const payload = {
          reason_type: this.selectedReasonType,
        }
        if (this.needsDateTime) payload.scheduled_date = this.scheduledDate || null
        if (this.needsAdditionalNote) payload.additional_note = this.additionalNote || ''
        const response = await axios.post(
          `/api/return-vouchers/${this.orderId}/create-return-reason/`,
          payload,
        )

        if (response.data.success) {
          // Don't show the toast here, it will be shown in the parent component
          this.$emit('reason-added')
          this.closeModal()
        }
      } catch (error) {
        console.error('Error creating return reason:', error)
        const errorMessage = error.response?.data?.message || 'Erreur lors de l\'ajout de la raison de retour'
        this.showToast('error', 'Erreur', errorMessage)
      } finally {
        this.isLoading = false
      }
    },

    closeModal() {
      this.$refs.addReturnReasonModal.hide()
    },

    resetForm() {
      this.selectedReasonType = null
      this.scheduledDate = null
      this.scheduledDateDate = null
      this.scheduledDateTime = null
      this.additionalNote = ''
      this.isLoading = false
    },

    getMinDate() {
      const now = new Date()
      return now.toISOString().slice(0, 10)
    },

    copyBlToClipboard() {
      if (this.orderBl) {
        navigator.clipboard.writeText(this.orderBl.toString())
        this.showToast('success', 'BL copié', `Le numéro BL ${this.orderBl} a été copié !`)
      }
    },

    showToast(variant, title, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          text,
          variant,
        },
      })
    },
  },
}
</script>

<style scoped>
.modal-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.modal-footer {
  background-color: #f8f9fa;
  border-top: 1px solid #dee2e6;
}
</style>
